import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONSIGNMENT_STATEMENT_MAX_LINES,
  consignmentStatementBlock,
  consignmentFeeChargeBlock,
  type ConsignmentFeeCharge,
  consignmentPaymentStatus,
  consignmentBankReceiptReference,
  type ConsignmentSaleAllocation,
  type ConsignmentStatement,
  type ConsignmentStatementDetail,
  type ConsignmentStatementMutation,
} from '@tcvs/shared';
import { ConsignmentFeeBreakdown } from './ConsignmentFeeBreakdown';
import { useConsignmentApi } from './consignment-api';
import { ConsignmentLedger } from './ConsignmentLedger';
import { ConsignmentPayments } from './ConsignmentPayments';
import { ConsignmentAccounting } from './ConsignmentAccounting';
import { ConsignmentBankReceiptReview } from './ConsignmentBankReceiptReview';
import { consignmentErrorMessage, formatMoneyMinor as money } from './consignment-ui';

export function ConsignmentStatements({ consignorId }: { consignorId: string }): JSX.Element {
  const api = useConsignmentApi();
  const [charges, setCharges] = useState<ConsignmentFeeCharge[]>([]);
  const [chargeCursor, setChargeCursor] = useState<string>();
  const [chargeIds, setChargeIds] = useState<string[]>([]);
  const [sales, setSales] = useState<ConsignmentSaleAllocation[]>([]);
  const [statements, setStatements] = useState<ConsignmentStatement[]>([]);
  const [saleCursor, setSaleCursor] = useState<string>();
  const [statementCursor, setStatementCursor] = useState<string>();
  const [ids, setIds] = useState<string[]>([]);
  const [detail, setDetail] = useState<ConsignmentStatementDetail>();
  const [mode, setMode] = useState<'sales' | 'statements'>('sales');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [fees, setFees] = useState(false);
  const [ack, setAck] = useState(false);
  const [bankReviewed, setBankReviewed] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const pending = useRef(false);
  const alive = useRef(true);
  const createId = useRef<{ signature: string; id: string }>();
  const reload = useCallback(async () => {
    const a = await api.listLedger(consignorId);
    const b = await api.listStatements(consignorId);
    const c = await api.listFeeCharges(consignorId);
    if (!c.ok) throw Error(consignmentErrorMessage(c));
    if (!a.ok) throw Error(consignmentErrorMessage(a));
    if (!b.ok) throw Error(consignmentErrorMessage(b));
    if (!alive.current) return;
    setCharges(c.data.items);
    setChargeCursor(c.data.cursor);
    setChargeIds([]);
    setSales(a.data.items);
    setSaleCursor(a.data.cursor);
    setStatements(b.data.items);
    setStatementCursor(b.data.cursor);
    setIds([]);
  }, [api, consignorId]);
  useEffect(() => {
    alive.current = true;
    void reload()
      .catch((e) => {
        if (alive.current) setError(String(e.message ?? e));
      })
      .finally(() => {
        if (alive.current) setBusy(false);
      });
    return () => {
      alive.current = false;
    };
  }, [reload]);
  async function run(action: () => Promise<void>): Promise<void> {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (e) {
      if (alive.current)
        setError(e instanceof Error ? e.message : 'Request failed. Refresh and retry.');
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  async function open(id: string): Promise<void> {
    const r = await api.getStatement(consignorId, id);
    if (!r.ok) throw Error(consignmentErrorMessage(r));
    if (alive.current) {
      setDetail(r.data);
      setFees(false);
      setAck(false);
      setBankReviewed(false);
      setBankReference('');
      setVoiding(false);
      setReason('');
      setMode('statements');
    }
  }
  async function mutate(body: ConsignmentStatementMutation): Promise<void> {
    const r = await api.mutate<ConsignmentStatement>(body);
    if (!r.ok) throw Error(consignmentErrorMessage(r));
    if (!alive.current) return;
    setNotice(
      r.data.status === 'APPROVED'
        ? 'Statement approved. No payment has been made.'
        : r.data.status === 'VOIDED'
          ? 'Draft voided. Its sources are available again when eligible.'
          : 'Draft created. Selected sales and charges are reserved.',
    );
    // The exact returned statement is authoritative even while its GSI entry catches up.
    await reload();
    setStatements((old) => [r.data, ...old.filter((s) => s.statementId !== r.data.statementId)]);
    await open(r.data.statementId);
  }
  const eligible = sales.filter((s) => !consignmentStatementBlock(s, new Date().toISOString()));
  const selectedTotal =
    sales
      .filter((s) => ids.includes(s.allocationId))
      .reduce((n, s) => n + (s.lifecycle?.estimatedProceedsMinor ?? 0), 0) -
    charges.filter((c) => chargeIds.includes(c.feeChargeId)).reduce((n, c) => n + c.amountMinor, 0);
  async function more(): Promise<void> {
    if (mode === 'sales') {
      const r = await api.listLedger(consignorId, saleCursor);
      if (!r.ok) throw Error(consignmentErrorMessage(r));
      if (alive.current) {
        setSales((old) => [
          ...old,
          ...r.data.items.filter((s) => !old.some((o) => o.allocationId === s.allocationId)),
        ]);
        setSaleCursor(r.data.cursor);
      }
    } else {
      const r = await api.listStatements(consignorId, statementCursor);
      if (!r.ok) throw Error(consignmentErrorMessage(r));
      if (alive.current) {
        setStatements((old) => [
          ...old,
          ...r.data.items.filter((s) => !old.some((o) => o.statementId === s.statementId)),
        ]);
        setStatementCursor(r.data.cursor);
      }
    }
  }
  return (
    <div className="tcvs-statements">
      <div className="tcvs-statement-toolbar">
        <div role="tablist" aria-label="Settlement views" className="tcvs-checklist-view-toggle">
          <button role="tab" aria-selected={mode === 'sales'} onClick={() => setMode('sales')}>
            Sales
          </button>
          <button
            role="tab"
            aria-selected={mode === 'statements'}
            onClick={() => setMode('statements')}
          >
            Statements
          </button>
        </div>
        <button
          disabled={busy}
          onClick={() =>
            void run(async () => {
              await reload();
              if (detail) await open(detail.statement.statementId);
            })
          }
        >
          Refresh
        </button>
      </div>
      {busy && <p role="status">Loading...</p>}
      {error && (
        <p className="tcvs-error" role="alert">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {mode === 'sales' ? (
        <>
          <div className="tcvs-statement-toolbar">
            <label>
              <input
                type="checkbox"
                aria-label="Select eligible loaded sales"
                disabled={busy || !eligible.length}
                checked={
                  eligible.length > 0 &&
                  ids.length === Math.min(eligible.length, CONSIGNMENT_STATEMENT_MAX_LINES)
                }
                onChange={(e) =>
                  setIds(
                    e.target.checked
                      ? eligible
                          .slice(0, CONSIGNMENT_STATEMENT_MAX_LINES - chargeIds.length)
                          .map((s) => s.allocationId)
                      : [],
                  )
                }
              />{' '}
              Select eligible loaded sales (up to 50)
            </label>
            <span>
              {ids.length + chargeIds.length} selected ·{' '}
              {selectedTotal < 0 ? `Recovery due ${money(-selectedTotal)}` : money(selectedTotal)}
            </span>
            <button
              disabled={busy || (!ids.length && !chargeIds.length)}
              onClick={() =>
                void run(async () => {
                  const signature = JSON.stringify({
                    sales: [...ids].sort(),
                    charges: [...chargeIds].sort(),
                  });
                  if (createId.current?.signature !== signature)
                    createId.current = { signature, id: crypto.randomUUID() };
                  await mutate({
                    operation: 'CREATE_STATEMENT',
                    consignorId,
                    statementId: createId.current.id,
                    allocationIds: ids,
                    ...(chargeIds.length ? { feeChargeIds: chargeIds } : {}),
                  });
                  createId.current = undefined;
                })
              }
            >
              Create draft statement
            </button>
          </div>
          <h3>Unsold charges</h3>
          <p>
            Charges reduce the statement balance. A zero or negative balance cannot be paid or
            collected automatically.
          </p>
          {charges.map((c) => {
            const block = consignmentFeeChargeBlock(c);
            return (
              <label key={c.feeChargeId} style={{ display: 'block' }}>
                <input
                  type="checkbox"
                  aria-label={`Select charge ${c.feeChargeId}`}
                  checked={chargeIds.includes(c.feeChargeId)}
                  disabled={
                    busy ||
                    !!block ||
                    (!chargeIds.includes(c.feeChargeId) && ids.length + chargeIds.length >= 50)
                  }
                  onChange={(e) =>
                    setChargeIds((old) =>
                      e.target.checked
                        ? [...old, c.feeChargeId]
                        : old.filter((id) => id !== c.feeChargeId),
                    )
                  }
                />
                {c.cardId} · {money(c.amountMinor)} · {block ?? 'Eligible'}
              </label>
            );
          })}
          {chargeCursor && (
            <button
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const r = await api.listFeeCharges(consignorId, chargeCursor);
                  if (!r.ok) throw Error(consignmentErrorMessage(r));
                  if (alive.current) {
                    setCharges((old) => [
                      ...old,
                      ...r.data.items.filter(
                        (c) => !old.some((o) => o.feeChargeId === c.feeChargeId),
                      ),
                    ]);
                    setChargeCursor(r.data.cursor);
                  }
                })
              }
            >
              Load more charges
            </button>
          )}
          <ConsignmentLedger
            ledger={sales}
            selection={{
              ids,
              disabled: busy,
              change: (id, checked) =>
                setIds((old) =>
                  checked
                    ? old.length + chargeIds.length < 50
                      ? [...old, id]
                      : old
                    : old.filter((x) => x !== id),
                ),
            }}
          />
        </>
      ) : (
        <>
          <h3>Statements ({statements.length} loaded)</h3>
          {!statements.length && !busy && <p>No statements.</p>}
          {statements.length > 0 && (
            <div className="tcvs-table-wrap">
              <table className="tcvs-table">
                <thead>
                  <tr>
                    <th>Statement</th>
                    <th>Created</th>
                    <th>Sales</th>
                    <th>Proceeds</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((s) => (
                    <tr key={s.statementId}>
                      <td>
                        <button
                          disabled={busy}
                          className="tcvs-link-button"
                          onClick={() => void run(() => open(s.statementId))}
                        >
                          {s.statementId.slice(-8)}
                        </button>
                      </td>
                      <td>{s.createdAt.slice(0, 10)}</td>
                      <td>
                        {s.lines.length} sales / {s.feeCharges?.length ?? 0} charges
                      </td>
                      <td>
                        {s.totals.estimatedProceedsMinor < 0
                          ? `Recovery due ${money(-s.totals.estimatedProceedsMinor)}`
                          : money(s.totals.estimatedProceedsMinor)}
                      </td>
                      <td>
                        {s.status === 'APPROVED'
                          ? consignmentPaymentStatus(s.financialSummary)
                          : s.status === 'DRAFT'
                            ? 'Draft'
                            : 'Voided'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {detail && (
            <section className="tcvs-statement-detail" aria-label="Statement detail">
              <h3>
                Statement {detail.statement.statementId.slice(-8)} ·{' '}
                {detail.statement.consignorName}
              </h3>
              <p>
                {detail.statement.status === 'APPROVED'
                  ? consignmentPaymentStatus(detail.statement.financialSummary)
                  : detail.statement.status}{' '}
                · USD · {detail.statement.lines.length} sales /{' '}
                {detail.statement.feeCharges?.length ?? 0} charges
              </p>
              <dl className="tcvs-detail-list">
                {Object.entries({
                  Gross: detail.statement.totals.grossAmountMinor,
                  Refunds: detail.statement.totals.refundAmountMinor,
                  Commission: detail.statement.totals.commissionAmountMinor,
                  'Fixed fees': detail.statement.totals.fixedFeeMinor,
                  'Unsold charges': detail.statement.totals.unsoldFeeAmountMinor ?? 0,
                  [detail.statement.totals.estimatedProceedsMinor < 0
                    ? 'Recovery due'
                    : 'Net proceeds']: Math.abs(detail.statement.totals.estimatedProceedsMinor),
                }).map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{money(value)}</dd>
                  </div>
                ))}
              </dl>
              {detail.issues.length > 0 && detail.statement.status !== 'APPROVED' && (
                <div role="alert">
                  <h4>Review required</h4>
                  <ul>
                    {detail.issues.map((i) => (
                      <li key={i.allocationId}>
                        {i.allocationId}: {i.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="tcvs-table-wrap">
                <table className="tcvs-table">
                  <thead>
                    <tr>
                      <th>Card / order</th>
                      <th>Gross</th>
                      <th>Refunds</th>
                      <th>Commission</th>
                      <th>Fixed fee</th>
                      <th>Proceeds</th>
                      <th>Order evidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.statement.lines.map((l) => (
                      <tr key={l.allocationId}>
                        <td>
                          <Link to={`/catalog/${l.cardId}`}>{l.title ?? l.cardId.slice(-8)}</Link>
                          <br />
                          {l.orderId} · {l.lineItemId}
                        </td>
                        <td>{money(l.grossAmountMinor)}</td>
                        <td>{money(l.lifecycle.refundAmountMinor)}</td>
                        <td>
                          {money(l.lifecycle.commissionAmountMinor)} ({l.commissionBps / 100}%)
                        </td>
                        <td>
                          {money(l.lifecycle.fixedFeeMinor)}
                          <ConsignmentFeeBreakdown calculation={l.lifecycle.feeCalculation} />
                        </td>
                        <td>{money(l.lifecycle.estimatedProceedsMinor)}</td>
                        <td>{l.lifecycle.sourceModifiedAt.replace('T', ' ').slice(0, 19)} UTC</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!!detail.statement.feeCharges?.length && (
                <section>
                  <h4>Unsold charge snapshots</h4>
                  <ul>
                    {detail.statement.feeCharges.map((c) => (
                      <li key={c.feeChargeId}>
                        {c.cardId} · {c.status} · Unsold {money(c.components.unsoldFeeMinor)} +
                        Listing {money(c.components.listingFeeMinor)} + Service{' '}
                        {money(c.components.serviceFeeMinor)} = {money(c.amountMinor)} ·{' '}
                        {c.evidenceReference}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {detail.statement.status === 'DRAFT' && (
                <div className="tcvs-statement-actions">
                  <ConsignmentBankReceiptReview
                    checked={bankReviewed}
                    reference={bankReference}
                    disabled={busy}
                    onChecked={setBankReviewed}
                    onReference={setBankReference}
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={fees}
                      disabled={busy}
                      onChange={(e) => setFees(e.target.checked)}
                    />{' '}
                    I reviewed the agreement terms and deductions; no unresolved fees remain.
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      checked={ack}
                      disabled={busy}
                      onChange={(e) => setAck(e.target.checked)}
                    />{' '}
                    I approve this snapshot of estimated proceeds. This does not record or send a
                    payment.
                  </label>
                  <div className="tcvs-statement-toolbar">
                    <button
                      disabled={
                        busy ||
                        !fees ||
                        !ack ||
                        !bankReviewed ||
                        !consignmentBankReceiptReference(bankReference) ||
                        detail.issues.length > 0
                      }
                      onClick={() =>
                        void run(() =>
                          mutate({
                            operation: 'APPROVE_STATEMENT',
                            consignorId,
                            statementId: detail.statement.statementId,
                            version: detail.statement.version,
                            feesReviewed: fees,
                            acknowledgeEstimates: ack,
                            bankReceiptReviewed: bankReviewed,
                            bankReceiptReference: bankReference.trim(),
                          }),
                        )
                      }
                    >
                      Approve statement
                    </button>
                    <button disabled={busy} onClick={() => setVoiding(!voiding)}>
                      Void draft
                    </button>
                  </div>
                  {voiding && (
                    <div>
                      <label>
                        Void reason{' '}
                        <textarea
                          maxLength={500}
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                      </label>
                      <button
                        disabled={busy || reason.trim().length < 3}
                        onClick={() =>
                          void run(() =>
                            mutate({
                              operation: 'VOID_STATEMENT',
                              consignorId,
                              statementId: detail.statement.statementId,
                              version: detail.statement.version,
                              reason,
                            }),
                          )
                        }
                      >
                        Confirm void and release sales
                      </button>
                    </div>
                  )}
                </div>
              )}
              {detail.statement.status === 'APPROVED' && (
                <ConsignmentPayments
                  key={detail.statement.statementId}
                  statement={detail.statement}
                  onSummary={(financialSummary) => {
                    setDetail((old) =>
                      old ? { ...old, statement: { ...old.statement, financialSummary } } : old,
                    );
                    setStatements((old) =>
                      old.map((s) =>
                        s.statementId === detail.statement.statementId
                          ? { ...s, financialSummary }
                          : s,
                      ),
                    );
                  }}
                />
              )}
              {detail.statement.status === 'APPROVED' && (
                <ConsignmentAccounting
                  key={`accounting-${detail.statement.statementId}`}
                  statement={detail.statement}
                />
              )}
              <h4>Approval history</h4>
              <ol>
                {detail.events.map((e) => (
                  <li key={e.version}>
                    {e.action} · {e.at.replace('T', ' ').slice(0, 19)} UTC · {e.actor}
                    {e.reason ? ` · ${e.reason}` : ''}
                    {e.bankReceiptReview
                      ? ` · Manual bank receipt evidence: ${e.bankReceiptReview.reference}`
                      : ''}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
      {(mode === 'sales' ? saleCursor : statementCursor) && (
        <button disabled={busy} onClick={() => void run(more)}>
          Load more {mode}
        </button>
      )}
    </div>
  );
}
